import React, { useState } from 'react';
import { Button, Spin, Empty, Dropdown, Input, Modal } from 'antd';
import {
  PlusOutlined,
  MessageOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { Conversation } from '../../../types/aiGenerate';
import { useTranslation } from '../../../hooks/useTranslation';
import './index.css';

interface ConversationListProps {
  conversations: Conversation[];
  currentId: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  currentId,
  loading,
  onSelect,
  onCreate,
  onDelete,
  onRename,
}) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  const handleStartEdit = (conversation: Conversation) => {
    setEditingId(conversation.id);
    setEditingTitle(conversation.title);
  };

  const handleSaveEdit = () => {
    if (editingId && editingTitle.trim()) {
      onRename(editingId, editingTitle.trim());
    }
    setEditingId(null);
    setEditingTitle('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingTitle('');
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: t('aiGenerate.confirmDelete'),
      content: t('aiGenerate.deleteConversationConfirm'),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: () => onDelete(id),
    });
  };

  const getMenuItems = (conversation: Conversation) => [
    {
      key: 'rename',
      icon: <EditOutlined />,
      label: t('aiGenerate.rename'),
      onClick: () => handleStartEdit(conversation),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('common.delete'),
      danger: true,
      onClick: () => handleDelete(conversation.id),
    },
  ];

  return (
    <div className="conversation-list">
      {/* 新建对话按钮 */}
      <div className="conversation-list-header">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          block
          onClick={onCreate}
          className="new-conversation-btn"
        >
          {t('aiGenerate.startNewConversation')}
        </Button>
      </div>

      {/* 对话列表 */}
      <div className="conversation-list-content">
        {loading ? (
          <div className="conversation-list-loading">
            <Spin />
          </div>
        ) : conversations.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('aiGenerate.noConversations')}
            className="conversation-list-empty"
          />
        ) : (
          <div className="conversation-items">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                className={`conversation-item ${
                  currentId === conversation.id ? 'active' : ''
                }`}
                onClick={() => {
                  if (editingId !== conversation.id) {
                    onSelect(conversation.id);
                  }
                }}
              >
                <div className="conversation-item-icon">
                  <MessageOutlined />
                </div>
                <div className="conversation-item-content">
                  {editingId === conversation.id ? (
                    <Input
                      size="small"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onPressEnter={handleSaveEdit}
                      onBlur={handleSaveEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          handleCancelEdit();
                        }
                      }}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="conversation-item-title">
                      {conversation.title}
                    </span>
                  )}
                </div>
                <div
                  className="conversation-item-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Dropdown
                    menu={{ items: getMenuItems(conversation) }}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreOutlined />}
                      className="conversation-more-btn"
                    />
                  </Dropdown>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
